import { memo, FC } from 'react';
import clsx from 'clsx';
import ContentWrapper from '@components/ContentWrapper';
import { useTranslation as t } from '@utils/hooks';
import * as RegPdpStyles from '@components/PDP/styles';
import useUiStore, {
  getDisplayMarketingBanner,
  getDisplayCart,
} from '@stores/use-ui-store';
import * as BuyButtonStyles from '@components/PDP/BuyButtonGroup/styles';
import Button from '@components/Button';
import {
  SubscriptionProductCollection,
  SubscriptionProductUpgradeOption,
} from 'fragile/types';
import { ConditionOption } from '../PdpConditionSelector/PdpConditionSelector';

type Props = {
  subscriptionProductName: string;
  shortDescription: string;
  selectedUpgradeOption: SubscriptionProductUpgradeOption;
  subscriptionProduct: SubscriptionProductCollection;
  isRegionallyAvailable: boolean;
  isVisible: boolean;
  selectedConditionOption: ConditionOption;
  isOutOfStockConditionMap: Record<ConditionOption, boolean>;
};

const BuySummary: FC<Props> = ({
  subscriptionProductName,
  shortDescription,
  selectedUpgradeOption,
  subscriptionProduct,
  isRegionallyAvailable,
  isVisible,
  selectedConditionOption,
  isOutOfStockConditionMap,
}) => {
  const displayMarketingBanner = useUiStore(getDisplayMarketingBanner);
  const displayCart = useUiStore(getDisplayCart);
  const SUBSCRIBE_CTA = t('subscribe_cta');
  const OUT_OF_STOCK = t('message_out_of_stock');

  const fragileSubscribeUrl = selectedUpgradeOption
    ? selectedUpgradeOption.fromCms.fragileCheckoutUrl
    : subscriptionProduct.products?.[0].upgrades?.[0].options?.[0]?.fromCms
        ?.fragileCheckoutUrl;

  const getUrl = (): string => {
    try {
      const initialUrl = sessionStorage.getItem('initialUrl');
      if (!initialUrl) {
        return fragileSubscribeUrl;
      }
      const userURL = new URL(initialUrl);
      const url = new URL(fragileSubscribeUrl);

      userURL.searchParams.forEach((value, key) => {
        url.searchParams.set(key, value);
      });

      // Add condition option upgrades to the URL
      if (selectedConditionOption === ConditionOption.GUARANTEED_NEW) {
        const upgrades = [
          {
            productId: selectedUpgradeOption.setupFeeProduct?.id,
            upgradeId: selectedUpgradeOption.setupFeeUpgrade?.id,
            optionId: selectedUpgradeOption.setupFeeUpgrade?.options?.[0].id,
          },
        ];
        // Base64 encode the upgrades array
        const upgradesBase64 = Buffer.from(JSON.stringify(upgrades)).toString(
          'base64'
        );
        url.searchParams.set('upgrades', upgradesBase64);
      }
      // Do nothing for LIKE_NEW condition option

      return url.toString();
    } catch (e) {
      return fragileSubscribeUrl;
    }
  };

  const isDisabled =
    !fragileSubscribeUrl || isOutOfStockConditionMap[selectedConditionOption];

  return (
    <div
      className={clsx(
        'bld-summary fixed flex left-0 right-0 bg-nzxt-light-grey-100 min-h-[100px] items-center dark:bg-purpleshame z-[49] transition-all lg:transition-none ease-in-out duration-200',
        {
          'top-0 lg:top-auto lg:bottom-0 md:flex lg:flex border-nzxt-light-grey-200 transform translate-y-0 opacity-100 lg:translate-y-0 lg:opacity-100':
            isVisible && displayMarketingBanner && !displayCart,
          'top-[101px] md:top-[109px] lg:top-auto lg:bottom-0 md:flex lg:flex border-nzxt-light-grey-200 transform translate-y-0 opacity-100 lg:translate-y-0 lg:opacity-100':
            isVisible && !displayMarketingBanner && !displayCart,
          'top-0 lg:top-auto lg:bottom-0 lg:flex transform translate-y-[-30px] md:translate-y-[-10px] lg:translate-y-0 opacity-0 lg:opacity-100':
            !isVisible && displayMarketingBanner && !displayCart,
          'md:top-[109px] lg:top-auto lg:bottom-0 lg:flex transform translate-y-[-30px] md:translate-y-[-10px] lg:translate-y-0 opacity-0 lg:opacity-100':
            !isVisible && !displayMarketingBanner && !displayCart,
          hidden: displayCart,
        }
      )}
    >
      <ContentWrapper>
        <div className="flex justify-between items-center lg:px-10">
          <div className="md:w-1/2 md:flex md:items-start items-center hidden flex-col">
            <div
              className="text-base font-bold"
              data-test-id="product-name-heading"
            >
              {subscriptionProductName}
            </div>
            <div className="text-xs" data-test-id="product-shrt-des">
              {shortDescription}
            </div>
          </div>
          <div className="md:w-1/2 flex justify-end items-center w-full">
            <div className="w-1/2 flex flex-col md:items-end">
              {isRegionallyAvailable && (
                <>
                  <div
                    className="text-base font-semibold space-x-2"
                    data-test-id="product-price"
                  >
                    <span
                      suppressHydrationWarning
                      data-test-id="product-price-amount"
                    >
                      {`$${Math.floor(
                        subscriptionProduct.monthlyPrice / 100
                      )}/mo`}
                    </span>
                  </div>
                  <span className={`text-xs ${RegPdpStyles.greyText}`}>
                    {t('subscription_pdp_nzxt_flex_support')}
                  </span>
                </>
              )}
            </div>
            <div className="flex w-1/2 flex-col px-8">
              <Button
                buttonStyle="primary"
                dataTestId="add-to-cart-button"
                type="button"
                disabled={isDisabled}
                onClick={() => {
                  window.location.href = getUrl();
                }}
                customMargin
                className={BuyButtonStyles.cartButton}
              >
                {isDisabled ? OUT_OF_STOCK : SUBSCRIBE_CTA}
              </Button>
            </div>
          </div>
        </div>
      </ContentWrapper>
    </div>
  );
};

export default memo(BuySummary);
